import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Row, Col, Tree, Button, Tag, Spin,
  message, Modal, Tooltip, Tabs, Upload, Form, Input, Select, Popconfirm,
} from 'antd';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import {
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileUnknownOutlined,
  UploadOutlined,
  DeleteOutlined,
  BookOutlined,
  SoundOutlined,
  VideoCameraOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import dayjs from 'dayjs';
import { opportunityApi } from '@/api/opportunityApi';
import type { OpportunityListItem } from '@/types/opportunity';
import { documentApi, type DocumentDto } from '@/api/documentApi';
import { knowledgeApi, type KnowledgeFolderDto, type KnowledgeArticleDto } from '@/api/knowledgeApi';
import axiosClient from '@/api/axiosClient';
import { useAuth } from '@/auth/useAuth';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

const { Text } = Typography;

interface FolderDto {
  id: string;
  name: string;
  folderType: string;
  phase: string;
  parentId: string;
  opportunityId: string;
}

const phaseLabels: Record<string, string> = {
  PHASE_A: 'Gate A', PHASE_B: 'Gate B', PHASE_C: 'Gate C',
  PHASE_D: 'Gate D', PHASE_E: 'Gate E', PHASE_F: 'Gate F',
};

/** Returns true for MIME types we can extract as raw text (CSV, JSON, XML…). */
const isTextExtractable = (t: string | null | undefined): boolean => {
  if (!t) return false;
  return (
    t === 'text/csv' ||
    t === 'application/json' ||
    t.startsWith('text/xml') ||
    t.startsWith('application/xml') ||
    t === 'text/markdown' ||
    t === 'text/javascript' ||
    t === 'application/javascript'
  );
};

/** Returns true for Word document MIME types. */
const isWordDoc = (t: string | null | undefined): boolean => {
  if (!t) return false;
  return (
    t === 'application/msword' ||
    t === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
};

/** Returns true for Excel spreadsheet MIME types. */
const isExcelDoc = (t: string | null | undefined): boolean => {
  if (!t) return false;
  return (
    t === 'application/vnd.ms-excel' ||
    t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    t === 'application/vnd.ms-excel.sheet.macroEnabled.12'
  );
};

/** Returns true for PowerPoint MIME types (server-side preview conversion). */
const isPptxDoc = (t: string | null | undefined): boolean => {
  if (!t) return false;
  return (
    t === 'application/vnd.ms-powerpoint' ||
    t === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  );
};

/**
 * Converts a DOCX blob to a styled HTML blob URL using mammoth.js.
 * Caller must revoke the returned URL when done.
 */
const docxToHtmlBlobUrl = async (blob: Blob): Promise<string> => {
  const arrayBuffer = await blob.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 13px;
         line-height: 1.7; color: #1a1a1a; padding: 32px 48px; max-width: 860px; margin: 0 auto; }
  h1,h2,h3,h4,h5,h6 { color: #111; margin-top: 1.2em; margin-bottom: 0.4em; }
  p { margin: 0.5em 0; }
  table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  td, th { border: 1px solid #d0d0d0; padding: 6px 10px; }
  th { background: #f0f4fa; font-weight: 600; }
  ul, ol { padding-left: 24px; }
  li { margin: 3px 0; }
  strong, b { font-weight: 600; }
  em, i { font-style: italic; }
</style>
</head>
<body>${result.value}</body>
</html>`;
  const htmlBlob = new Blob([html], { type: 'text/html' });
  return URL.createObjectURL(htmlBlob);
};

/**
 * Converts an Excel blob to an HTML table blob URL using SheetJS.
 * Caller must revoke the returned URL when done.
 */
const excelToHtmlBlobUrl = async (blob: Blob): Promise<string> => {
  const arrayBuffer = await blob.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetNames = workbook.SheetNames;
  let body = '';
  sheetNames.forEach((name) => {
    const sheet = workbook.Sheets[name];
    const tableHtml = XLSX.utils.sheet_to_html(sheet, { id: `sheet-${name}` });
    body += `<h3 style="margin:20px 0 8px;color:#1a1a1a;font-family:sans-serif;font-size:14px;">
      Sheet: ${name}</h3>${tableHtml}`;
  });
  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 12px;
         line-height: 1.4; color: #1a1a1a; padding: 16px 24px; }
  table { border-collapse: collapse; width: max-content; min-width: 100%; margin: 0 0 24px; }
  td, th { border: 1px solid #d0d0d0; padding: 4px 10px; white-space: nowrap; }
  th { background: #e8f0fe; font-weight: 600; position: sticky; top: 0; }
  tr:nth-child(even) td { background: #f8f9fa; }
</style>
</head>
<body>${body}</body>
</html>`;
  const htmlBlob = new Blob([html], { type: 'text/html' });
  return URL.createObjectURL(htmlBlob);
};

/** Icon matched to MIME type. */
const FileTypeIcon = ({ fileType }: { fileType: string }) => {
  if (fileType === 'application/pdf') return <FilePdfOutlined style={{ color: '#E53E3E', marginRight: 6 }} />;
  if (fileType?.startsWith('image/')) return <FileImageOutlined style={{ color: '#38A169', marginRight: 6 }} />;
  if (fileType?.startsWith('video/')) return <VideoCameraOutlined style={{ color: '#7B2D8B', marginRight: 6 }} />;
  if (fileType?.startsWith('audio/')) return <SoundOutlined style={{ color: '#0EA5E9', marginRight: 6 }} />;
  if (fileType?.includes('word')) return <FileWordOutlined style={{ color: '#2B6CB0', marginRight: 6 }} />;
  if (fileType?.includes('sheet') || fileType?.includes('excel')) return <FileExcelOutlined style={{ color: '#276749', marginRight: 6 }} />;
  if (fileType === 'text/plain' || fileType === 'text/csv') return <FileOutlined style={{ color: '#718096', marginRight: 6 }} />;
  return <FileUnknownOutlined style={{ color: '#A0AEC0', marginRight: 6 }} />;
};

/** Returns the CSS modifier class for the file-type icon background. */
const fileTypeIconClass = (fileType: string | null | undefined): string => {
  if (!fileType) return 'cl-file-card__icon--unknown';
  if (fileType === 'application/pdf') return 'cl-file-card__icon--pdf';
  if (fileType.startsWith('image/')) return 'cl-file-card__icon--image';
  if (fileType.startsWith('video/')) return 'cl-file-card__icon--video';
  if (fileType.startsWith('audio/')) return 'cl-file-card__icon--audio';
  if (fileType.includes('word')) return 'cl-file-card__icon--word';
  if (fileType.includes('sheet') || fileType.includes('excel')) return 'cl-file-card__icon--excel';
  if (fileType === 'text/plain' || fileType === 'text/csv') return 'cl-file-card__icon--text';
  return 'cl-file-card__icon--unknown';
};

/** Large file-type icon used inside the icon grid cards. */
const FileTypeIconLarge = ({ fileType }: { fileType: string | null | undefined }) => {
  if (!fileType) return <FileUnknownOutlined />;
  if (fileType === 'application/pdf') return <FilePdfOutlined />;
  if (fileType.startsWith('image/')) return <FileImageOutlined />;
  if (fileType.startsWith('video/')) return <VideoCameraOutlined />;
  if (fileType.startsWith('audio/')) return <SoundOutlined />;
  if (fileType.includes('word')) return <FileWordOutlined />;
  if (fileType.includes('sheet') || fileType.includes('excel')) return <FileExcelOutlined />;
  if (fileType === 'text/plain' || fileType === 'text/csv') return <FileOutlined />;
  return <FileUnknownOutlined />;
};

const buildTree = (folders: FolderDto[]): DataNode[] => {
  const buildNode = (folder: FolderDto): DataNode => {
    const children = folders.filter((f) => f.parentId === folder.id);
    return {
      key: folder.id,
      title: folder.name,
      icon: ({ expanded }: { expanded?: boolean }) =>
        expanded ? <FolderOpenOutlined /> : <FolderOutlined />,
      children: children.length > 0 ? children.map(buildNode) : undefined,
      isLeaf: children.length === 0,
    };
  };
  const roots = folders.filter((f) => !f.parentId);
  // Skip ROOT folder — show phase folders directly to avoid duplicate opportunity name
  if (roots.length === 1 && roots[0].folderType === 'ROOT') {
    const children = folders.filter((f) => f.parentId === roots[0].id);
    return children.map(buildNode);
  }
  return roots.map(buildNode);
};

const formatSize = (bytes: number) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Reusable Preview Modal — handles all file types
// ─────────────────────────────────────────────────────────────────────────────
interface PreviewState {
  fileName: string;
  fileType: string;
  fileSizeBytes?: number;
}

const PreviewModal = ({
  previewItem,
  blobUrl,
  convertedUrl,
  textContent,
  loading,
  onClose,
  onDownload,
}: {
  previewItem: PreviewState | null;
  blobUrl: string | null;
  /** Blob URL of a mammoth/SheetJS-converted HTML document — rendered in an iframe. */
  convertedUrl?: string | null;
  textContent?: string | null;
  loading: boolean;
  onClose: () => void;
  onDownload?: () => void;
}) => {
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            Loading preview...
          </Text>
        </div>
      );
    }
    if (!previewItem) return null;

    const { fileType, fileName } = previewItem;

    // ── Word / Excel converted to HTML via mammoth/SheetJS ───────────────────
    if (convertedUrl) {
      return (
        <iframe
          src={convertedUrl}
          title={fileName}
          sandbox="allow-same-origin"
          style={{ width: '100%', height: 'calc(80vh - 80px)', border: 'none', borderRadius: 4 }}
        />
      );
    }

    // ── Images ───────────────────────────────────────────────────────────────
    if (fileType?.startsWith('image/') && blobUrl) {
      return (
        <div style={{ textAlign: 'center' }}>
          <img
            src={blobUrl}
            alt={fileName}
            style={{ maxWidth: '100%', maxHeight: 'calc(80vh - 80px)', objectFit: 'contain', borderRadius: 4 }}
          />
        </div>
      );
    }

    // ── PDF / plain text / HTML → iframe ─────────────────────────────────────
    if (blobUrl && (
      fileType === 'application/pdf' ||
      fileType === 'text/plain' ||
      fileType === 'text/html'
    )) {
      return (
        <iframe
          src={blobUrl}
          title={fileName}
          style={{ width: '100%', height: 'calc(80vh - 80px)', border: 'none', borderRadius: 4 }}
        />
      );
    }

    // ── Extractable text (CSV, JSON, XML, Markdown, JS) → <pre> ──────────────
    if (textContent !== null && textContent !== undefined) {
      return (
        <pre
          style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: 16,
            borderRadius: 4,
            overflow: 'auto',
            maxHeight: 'calc(80vh - 80px)',
            fontSize: 12,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {textContent}
        </pre>
      );
    }

    // ── Video ────────────────────────────────────────────────────────────────
    if (fileType?.startsWith('video/') && blobUrl) {
      return (
        <div style={{ textAlign: 'center' }}>
          <video
            controls
            src={blobUrl}
            style={{ maxWidth: '100%', maxHeight: 'calc(80vh - 80px)', borderRadius: 4 }}
          />
        </div>
      );
    }

    // ── Audio ────────────────────────────────────────────────────────────────
    if (fileType?.startsWith('audio/') && blobUrl) {
      return (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <SoundOutlined style={{ fontSize: 48, color: '#0EA5E9', marginBottom: 16 }} />
          <div>
            <audio controls src={blobUrl} style={{ width: '100%', maxWidth: 480 }} />
          </div>
        </div>
      );
    }

    // ── Fallback: cannot preview inline (PPT, ZIP, etc.) ─────────────────────
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px' }}>
        <FileUnknownOutlined style={{ fontSize: 48, color: '#A0AEC0', marginBottom: 16 }} />
        <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
          This file format cannot be previewed in the browser.
        </Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 12 }}>
          {fileType || 'Unknown type'} · {previewItem?.fileSizeBytes ? formatSize(previewItem.fileSizeBytes) : ''}
        </Text>
        {onDownload && (
          <Button type="primary" icon={<DownloadOutlined />} onClick={onDownload}>
            Download to open
          </Button>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={!!previewItem}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {previewItem?.fileName}
            {previewItem?.fileSizeBytes ? ` · ${formatSize(previewItem.fileSizeBytes)}` : ''}
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            {onDownload && (
              <Button icon={<DownloadOutlined />} onClick={onDownload}>
                Download
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      }
      title={
        <span>
          {previewItem && <FileTypeIcon fileType={previewItem.fileType} />}
          {previewItem?.fileName ?? 'Preview'}
        </span>
      }
      width="80vw"
      styles={{ body: { padding: 16 } }}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Knowledge Base Tab
// ─────────────────────────────────────────────────────────────────────────────
const KnowledgeBaseTab = () => {
  const { hasAnyRole } = useAuth();
  const canManage = hasAnyRole(['ADMIN', 'CEO']);

  const [folders, setFolders] = useState<KnowledgeFolderDto[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState<KnowledgeFolderDto | null>(null);
  const [articles, setArticles] = useState<KnowledgeArticleDto[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);

  // Upload modal
  const [uploadVisible, setUploadVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm] = Form.useForm();
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Preview
  const [previewArticle, setPreviewArticle] = useState<KnowledgeArticleDto | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewConvertedUrl, setPreviewConvertedUrl] = useState<string | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      if (previewConvertedUrl) URL.revokeObjectURL(previewConvertedUrl);
    };
  }, [previewBlobUrl, previewConvertedUrl]);

  const fetchFolders = async () => {
    setLoadingFolders(true);
    try {
      const data = await knowledgeApi.getFolders();
      setFolders(data);
    } catch {
      message.error('Failed to load knowledge folders');
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleSelectFolder = async (folder: KnowledgeFolderDto) => {
    setSelectedFolder(folder);
    setLoadingArticles(true);
    try {
      const data = await knowledgeApi.getArticlesByFolder(folder.id);
      setArticles(data);
    } catch {
      setArticles([]);
    } finally {
      setLoadingArticles(false);
    }
  };

  const handlePreview = useCallback(async (article: KnowledgeArticleDto) => {
    if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
    if (previewConvertedUrl) { URL.revokeObjectURL(previewConvertedUrl); setPreviewConvertedUrl(null); }
    setPreviewTextContent(null);
    setPreviewArticle(article);
    setPreviewLoading(true);
    try {
      if (isPptxDoc(article.fileType)) {
        // PPTX: use server-side conversion to HTML with slide images
        const response = await axiosClient.get(`/knowledge/articles/${article.id}/stream`, {
          params: { preview: true },
          responseType: 'blob',
        });
        const htmlBlob = new Blob([response.data], { type: 'text/html' });
        setPreviewConvertedUrl(URL.createObjectURL(htmlBlob));
      } else {
        const response = await axiosClient.get(`/knowledge/articles/${article.id}/stream`, {
          responseType: 'blob',
        });
        const blob: Blob = response.data;
        setPreviewBlobUrl(URL.createObjectURL(blob));
        if (isWordDoc(article.fileType)) {
          setPreviewConvertedUrl(await docxToHtmlBlobUrl(blob));
        } else if (isExcelDoc(article.fileType)) {
          setPreviewConvertedUrl(await excelToHtmlBlobUrl(blob));
        } else if (isTextExtractable(article.fileType)) {
          setPreviewTextContent(await blob.text());
        }
      }
    } catch {
      message.error('Failed to load preview');
      setPreviewArticle(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewBlobUrl, previewConvertedUrl]);

  const closePreview = useCallback(() => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    if (previewConvertedUrl) URL.revokeObjectURL(previewConvertedUrl);
    setPreviewBlobUrl(null);
    setPreviewConvertedUrl(null);
    setPreviewTextContent(null);
    setPreviewArticle(null);
  }, [previewBlobUrl, previewConvertedUrl]);

  const handleDownloadArticle = async (article: KnowledgeArticleDto) => {
    try {
      await knowledgeApi.downloadArticle(article.id, article.fileName ?? article.title);
    } catch {
      message.error('Failed to download file');
    }
  };

  const handleDelete = async (article: KnowledgeArticleDto) => {
    try {
      await knowledgeApi.deleteArticle(article.id);
      message.success('Article deleted');
      if (selectedFolder) {
        const data = await knowledgeApi.getArticlesByFolder(selectedFolder.id);
        setArticles(data);
        fetchFolders();
      }
    } catch {
      message.error('Failed to delete article');
    }
  };

  const handleUploadSubmit = async () => {
    if (!pendingFile) {
      message.error('Please select a file');
      return;
    }
    const values = uploadForm.getFieldsValue();
    setUploading(true);
    try {
      await knowledgeApi.uploadArticle(
        pendingFile,
        values.folderId || null,
        values.title || undefined,
        values.description || undefined,
      );
      message.success('Article uploaded successfully');
      setUploadVisible(false);
      uploadForm.resetFields();
      setPendingFile(null);
      if (selectedFolder) {
        const data = await knowledgeApi.getArticlesByFolder(selectedFolder.id);
        setArticles(data);
        fetchFolders();
      }
    } catch {
      message.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /** Renders the articles as an icon grid instead of a table. */
  const renderArticleGrid = () => (
    <div className="cl-file-grid ui-stagger">
      {articles.map((article) => (
        <div
          key={article.id}
          className="ui-surface ui-surface--interactive cl-file-card"
          onClick={() => handlePreview(article)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePreview(article); }}
        >
          <span className={`cl-file-card__icon ${fileTypeIconClass(article.fileType)}`}>
            <FileTypeIconLarge fileType={article.fileType} />
          </span>
          <span className="cl-file-card__name">
            {article.fileName ?? article.title}
          </span>
          <span className="cl-file-card__meta">
            {article.fileSizeBytes ? formatSize(article.fileSizeBytes) : ''}
            {article.fileSizeBytes && article.createdAt ? ' · ' : ''}
            {article.createdAt ? dayjs(article.createdAt).format('MM/DD/YY') : ''}
          </span>
          <span className="cl-file-card__actions" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Preview">
              <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => handlePreview(article)} />
            </Tooltip>
            <Tooltip title="Download">
              <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => handleDownloadArticle(article)} />
            </Tooltip>
            {canManage && (
              <Popconfirm
                title="Delete this article?"
                onConfirm={() => handleDelete(article)}
                okText="Delete"
                okType="danger"
              >
                <Tooltip title="Delete">
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {selectedFolder ? (
        // ── Detail view: articles in the chosen folder ─────────────────────────
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => { setSelectedFolder(null); setArticles([]); }}
            >
              All folders
            </Button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                {selectedFolder.name}
              </div>
              {selectedFolder.description && (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                  {selectedFolder.description}
                </div>
              )}
            </div>
            {canManage && (
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadVisible(true)}>
                Upload
              </Button>
            )}
          </div>

          <div className="ui-surface" style={{ minHeight: 400, padding: 12 }}>
            {loadingArticles ? (
              <div className="ui-stagger">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="ui-skeleton" style={{ height: 36, margin: '8px 0', borderRadius: 8 }} />
                ))}
              </div>
            ) : articles.length === 0 ? (
              <EmptyState
                icon={<FileOutlined />}
                title="No articles yet"
                description={`There are no articles in "${selectedFolder.name}" yet.`}
              />
            ) : (
              renderArticleGrid()
            )}
          </div>
        </div>
      ) : (
        // ── Grid view: knowledge folders as cards ──────────────────────────────
        <div>
          {canManage && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadVisible(true)}>
                Upload Article
              </Button>
            </div>
          )}

          {loadingFolders ? (
            <div className="cl-card-grid ui-stagger">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="ui-skeleton" style={{ height: 152, borderRadius: 16 }} />
              ))}
            </div>
          ) : folders.length === 0 ? (
            <EmptyState
              icon={<BookOutlined />}
              title="No folders"
              description="Knowledge folders will appear here."
            />
          ) : (
            <div className="cl-card-grid ui-stagger">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className="ui-surface ui-surface--interactive cl-opp-card"
                  onClick={() => handleSelectFolder(folder)}
                >
                  <span className="cl-opp-card__icon">
                    <BookOutlined />
                  </span>
                  <span className="cl-opp-card__title">{folder.name}</span>
                  {folder.description && (
                    <span className="cl-opp-card__desc">{folder.description}</span>
                  )}
                  <span className="cl-opp-card__meta">
                    <span className="cl-opp-card__count">
                      <FileOutlined />
                      {folder.articleCount} article{folder.articleCount !== 1 ? 's' : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      <Modal
        open={uploadVisible}
        title="Upload Knowledge Article"
        onCancel={() => { setUploadVisible(false); uploadForm.resetFields(); setPendingFile(null); }}
        onOk={handleUploadSubmit}
        okText="Upload"
        confirmLoading={uploading}
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Folder" name="folderId">
            <Select placeholder="Select folder (optional)" allowClear>
              {folders.map((f) => (
                <Select.Option key={f.id} value={f.id}>{f.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Title" name="title">
            <Input placeholder="Article title (defaults to file name)" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Brief description" />
          </Form.Item>
          <Form.Item label="File" required>
            <Upload
              beforeUpload={(file) => {
                setPendingFile(file);
                return false;
              }}
              maxCount={1}
              onRemove={() => setPendingFile(null)}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <PreviewModal
        previewItem={
          previewArticle
            ? {
                fileName: previewArticle.fileName ?? previewArticle.title,
                fileType: previewArticle.fileType ?? '',
                fileSizeBytes: previewArticle.fileSizeBytes,
              }
            : null
        }
        blobUrl={previewBlobUrl}
        convertedUrl={previewConvertedUrl}
        textContent={previewTextContent}
        loading={previewLoading}
        onClose={closePreview}
        onDownload={previewArticle ? () => handleDownloadArticle(previewArticle) : undefined}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
const ContentLibraryPage = () => {
  // All authenticated users can delete documents
  const canDeleteDoc = (_doc: DocumentDto) => true;

  const [opportunities, setOpportunities] = useState<OpportunityListItem[]>([]);
  const [loadingOpps, setLoadingOpps] = useState(true);
  const [oppSearch, setOppSearch] = useState('');
  // Document counts per opportunity, fetched lazily in the background for the cards.
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});
  const [selectedOppId, setSelectedOppId] = useState<string | null>(null);
  const [selectedOppName, setSelectedOppName] = useState('');
  const [folders, setFolders] = useState<FolderDto[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<FolderDto | null>(null);
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // ── Preview state ─────────────────────────────────────────────────────────
  const [previewDoc, setPreviewDoc] = useState<DocumentDto | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewConvertedUrl, setPreviewConvertedUrl] = useState<string | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  // Lazily fetch per-opportunity document counts for the card badges. Runs after
  // the grid has rendered so it never blocks the initial paint.
  useEffect(() => {
    if (opportunities.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        opportunities.map(async (opp) => {
          try {
            const docs = await documentApi.getByOpportunityId(opp.id);
            return [opp.id, docs.length] as const;
          } catch {
            return [opp.id, 0] as const;
          }
        }),
      );
      if (!cancelled) setDocCounts(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [opportunities]);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      if (previewConvertedUrl) URL.revokeObjectURL(previewConvertedUrl);
    };
  }, [previewBlobUrl, previewConvertedUrl]);

  const fetchOpportunities = async () => {
    setLoadingOpps(true);
    try {
      const response = await opportunityApi.getAll({ size: 100 });
      setOpportunities(response.content);
    } catch {
      message.error('Failed to load opportunities');
    } finally {
      setLoadingOpps(false);
    }
  };

  const handleSelectOpportunity = async (opp: OpportunityListItem) => {
    if (selectedOppId === opp.id) return;
    setSelectedOppId(opp.id);
    setSelectedOppName(opp.title);
    setSelectedFolder(null);
    setDocuments([]);
    setFolders([]);
    setLoadingFolders(true);
    try {
      const response = await axiosClient.get(`/opportunities/${opp.id}/folders`);
      setFolders(response.data);
    } catch {
      message.error('Failed to load folders');
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleFolderSelect = async (selectedKeys: React.Key[]) => {
    if (selectedKeys.length === 0) return;
    const folderId = selectedKeys[0] as string;
    const folder = folders.find((f) => f.id === folderId);
    if (!folder || !selectedOppId) return;
    setSelectedFolder(folder);
    if (!folder.phase) {
      setDocuments([]);
      return;
    }
    setLoadingDocs(true);
    try {
      // For subfolders (non-ROOT/PHASE), load by folderId to get folder-specific docs
      const isSubfolder = folder.folderType && folder.folderType !== 'ROOT' && folder.folderType !== 'PHASE';
      let docs;
      if (isSubfolder) {
        docs = await documentApi.getByFolder(folder.id);
      } else {
        docs = await documentApi.getByPhase(selectedOppId, folder.phase);
      }
      setDocuments(docs);
    } catch {
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleDownload = async (doc: DocumentDto) => {
    try {
      await documentApi.downloadFile(doc.id, doc.fileName);
    } catch {
      message.error('Failed to download file');
    }
  };

  const handlePreview = useCallback(async (doc: DocumentDto) => {
    if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
    if (previewConvertedUrl) { URL.revokeObjectURL(previewConvertedUrl); setPreviewConvertedUrl(null); }
    setPreviewTextContent(null);
    setPreviewDoc(doc);
    setPreviewLoading(true);
    try {
      if (isPptxDoc(doc.fileType)) {
        // PPTX: use server-side conversion to HTML with slide images
        const response = await axiosClient.get(`/documents/${doc.id}/stream`, {
          params: { preview: true },
          responseType: 'blob',
        });
        const htmlBlob = new Blob([response.data], { type: 'text/html' });
        setPreviewConvertedUrl(URL.createObjectURL(htmlBlob));
      } else {
        const response = await axiosClient.get(`/documents/${doc.id}/stream`, {
          responseType: 'blob',
        });
        const blob: Blob = response.data;
        setPreviewBlobUrl(URL.createObjectURL(blob));
        if (isWordDoc(doc.fileType)) {
          setPreviewConvertedUrl(await docxToHtmlBlobUrl(blob));
        } else if (isExcelDoc(doc.fileType)) {
          setPreviewConvertedUrl(await excelToHtmlBlobUrl(blob));
        } else if (isTextExtractable(doc.fileType)) {
          setPreviewTextContent(await blob.text());
        }
      }
    } catch {
      message.error('Failed to load file preview');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewBlobUrl, previewConvertedUrl]);

  const closePreview = useCallback(() => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    if (previewConvertedUrl) URL.revokeObjectURL(previewConvertedUrl);
    setPreviewBlobUrl(null);
    setPreviewConvertedUrl(null);
    setPreviewTextContent(null);
    setPreviewDoc(null);
  }, [previewBlobUrl, previewConvertedUrl]);

  const handleDeleteDoc = async (doc: DocumentDto) => {
    try {
      await documentApi.deleteDocument(doc.id);
      message.success('File deleted');
      if (selectedOppId && selectedFolder?.phase) {
        const docs = await documentApi.getByPhase(selectedOppId, selectedFolder.phase);
        setDocuments(docs);
      }
    } catch {
      message.error('Failed to delete file');
    }
  };

  const treeData = buildTree(folders);

  /** Renders documents as an icon grid. */
  const renderDocGrid = () => (
    <div className="cl-file-grid ui-stagger">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="ui-surface ui-surface--interactive cl-file-card"
          onClick={() => handlePreview(doc)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePreview(doc); }}
        >
          <span className={`cl-file-card__icon ${fileTypeIconClass(doc.fileType)}`}>
            <FileTypeIconLarge fileType={doc.fileType} />
          </span>
          <span className="cl-file-card__name">{doc.fileName}</span>
          <span className="cl-file-card__meta">
            {formatSize(doc.fileSizeBytes)}
            {doc.createdAt ? ` · ${dayjs(doc.createdAt).format('MM/DD/YY')}` : ''}
          </span>
          <span className="cl-file-card__actions" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Preview">
              <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => handlePreview(doc)} />
            </Tooltip>
            <Tooltip title="Download">
              <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(doc)} />
            </Tooltip>
            {canDeleteDoc(doc) && (
              <Popconfirm
                title="Delete this file?"
                onConfirm={() => handleDeleteDoc(doc)}
                okText="Delete"
                okType="danger"
              >
                <Tooltip title="Delete">
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </span>
        </div>
      ))}
    </div>
  );

  const q = oppSearch.trim().toLowerCase();
  const filteredOpps = q
    ? opportunities.filter(
        (o) =>
          o.title.toLowerCase().includes(q) ||
          (o.agencyName || '').toLowerCase().includes(q),
      )
    : opportunities;

  const backToGrid = () => {
    setSelectedOppId(null);
    setSelectedOppName('');
    setSelectedFolder(null);
    setDocuments([]);
    setFolders([]);
    setOppSearch('');
  };

  // ── Grid view: browse opportunities as cards ───────────────────────────────
  const gridView = (
    <div>
      <div style={{ marginBottom: 16, maxWidth: 360 }}>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
          placeholder="Search opportunities…"
          value={oppSearch}
          onChange={(e) => setOppSearch(e.target.value)}
        />
      </div>

      {loadingOpps ? (
        <div className="cl-card-grid ui-stagger">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="ui-skeleton" style={{ height: 152, borderRadius: 16 }} />
          ))}
        </div>
      ) : filteredOpps.length === 0 ? (
        <EmptyState
          icon={<FolderOutlined />}
          title={oppSearch ? 'No matches' : 'No opportunities'}
          description={
            oppSearch
              ? 'Try a different search term.'
              : 'Opportunities will appear here once created.'
          }
        />
      ) : (
        <div className="cl-card-grid ui-stagger">
          {filteredOpps.map((opp) => {
            const count = docCounts[opp.id];
            return (
              <button
                key={opp.id}
                type="button"
                className="ui-surface ui-surface--interactive cl-opp-card"
                onClick={() => handleSelectOpportunity(opp)}
              >
                <span className="cl-opp-card__icon">
                  <FolderOutlined />
                </span>
                <span className="cl-opp-card__title">{opp.title}</span>
                <span className="cl-opp-card__agency">{opp.agencyName || 'No agency'}</span>
                <span className="cl-opp-card__meta">
                  <span className="cl-opp-card__count">
                    <FileOutlined />
                    {count === undefined
                      ? '—'
                      : `${count} file${count === 1 ? '' : 's'}`}
                  </span>
                  {opp.currentPhase && (
                    <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 11 }}>
                      {phaseLabels[opp.currentPhase] || opp.currentPhase}
                    </Tag>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Detail view: folders + documents for the chosen opportunity ────────────
  const detailView = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={backToGrid}>
          All opportunities
        </Button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {selectedOppName}
          </div>
          {opportunities.find((o) => o.id === selectedOppId)?.agencyName && (
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {opportunities.find((o) => o.id === selectedOppId)?.agencyName}
            </div>
          )}
        </div>
      </div>

      <Row gutter={16}>
        {/* Left: folder tree */}
        <Col xs={24} md={7}>
          <div className="ui-surface" style={{ minHeight: 480, display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid var(--color-border-light)',
              fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)',
            }}>
              Folders
            </div>
            <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)', flex: 1, padding: 8 }}>
              {loadingFolders ? (
                <div style={{ padding: 8 }}>
                  <Spin size="small" />
                </div>
              ) : treeData.length > 0 ? (
                <Tree
                  showIcon
                  defaultExpandAll
                  treeData={treeData}
                  onSelect={handleFolderSelect}
                  style={{ fontSize: 13 }}
                />
              ) : (
                <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                  No folders yet
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* Right: documents */}
        <Col xs={24} md={17}>
          <div className="ui-surface" style={{ minHeight: 480 }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid var(--color-border-light)',
              fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)',
            }}>
              {selectedFolder ? selectedFolder.name : 'Select a folder to view documents'}
            </div>
            <div style={{ padding: 12 }}>
              {!selectedFolder ? (
                <EmptyState
                  icon={<FolderOutlined />}
                  title="No folder selected"
                  description="Select a folder from the left to view its documents."
                />
              ) : loadingDocs ? (
                <div className="ui-stagger">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="ui-skeleton" style={{ height: 36, margin: '8px 0', borderRadius: 8 }} />
                  ))}
                </div>
              ) : documents.length === 0 ? (
                <EmptyState
                  icon={<FileOutlined />}
                  title="No documents"
                  description={`There are no documents in "${selectedFolder.name}".`}
                />
              ) : (
                renderDocGrid()
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  const opportunityFilesTab = selectedOppId ? detailView : gridView;

  return (
    <div>
      <PageHeader
        title="Content Library"
        subtitle={`${opportunities.length} opportunit${opportunities.length === 1 ? 'y' : 'ies'} · documents & knowledge base`}
        actions={
          <Button icon={<ReloadOutlined />} onClick={fetchOpportunities}>
            Refresh
          </Button>
        }
      />

      <Tabs
        defaultActiveKey="opportunity-files"
        items={[
          {
            key: 'opportunity-files',
            label: (
              <span>
                <FolderOutlined />
                Opportunity Files
              </span>
            ),
            children: opportunityFilesTab,
          },
          {
            key: 'knowledge-base',
            label: (
              <span>
                <BookOutlined />
                Knowledge Base
              </span>
            ),
            children: <KnowledgeBaseTab />,
          },
        ]}
      />

      {/* ── Inline Preview Modal (for Opportunity Files tab) ─────────────────── */}
      <PreviewModal
        previewItem={
          previewDoc
            ? {
                fileName: previewDoc.fileName,
                fileType: previewDoc.fileType,
                fileSizeBytes: previewDoc.fileSizeBytes,
              }
            : null
        }
        blobUrl={previewBlobUrl}
        convertedUrl={previewConvertedUrl}
        textContent={previewTextContent}
        loading={previewLoading}
        onClose={closePreview}
        onDownload={previewDoc ? () => handleDownload(previewDoc) : undefined}
      />
    </div>
  );
};

export default ContentLibraryPage;
